import { FC, ReactNode } from 'react';
import { Button, cn } from '@crisellevaldez/speculo-ui';
import { SlidersHorizontal } from 'lucide-react';

interface FilterButtonProps {
  onClick: () => void;
  /** Number of currently-applied filters. When > 0 a colored badge renders. */
  activeCount: number;
  /** Display label next to the icon. Defaults to "Filter". */
  label?: string;
  /** speculo-ui Button variant. Defaults to "secondary" to match existing table headers. */
  variant?: 'outline' | 'secondary' | 'primary' | 'ghost';
  /** Extra classes for the button — pass layout/width here. */
  className?: string;
  /** Hide the label below the `sm` breakpoint (icon-only on small screens). */
  hideLabelOnMobile?: boolean;
  /** Override the icon. */
  icon?: ReactNode;
}

/**
 * Reusable "Filter" button with a colored active-count badge. Designed to
 * match across surfaces so every filter affordance reads the same — see
 * EngageTableHeader, ReviveTableHeader, CallStatsHeader (mobile).
 *
 * The button is purely a trigger; consumers own the modal/popover that
 * opens when it's clicked.
 */
export const FilterButton: FC<FilterButtonProps> = ({
  onClick,
  activeCount,
  label = 'Filter',
  variant = 'secondary',
  className,
  hideLabelOnMobile = false,
  icon,
}) => {
  return (
    <Button
      variant={variant}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 relative',
        className,
      )}
    >
      {icon ?? <SlidersHorizontal className="h-4 w-4" />}
      <span className={hideLabelOnMobile ? 'hidden sm:inline' : undefined}>
        {label}
      </span>
      {activeCount > 0 && (
        <span
          className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none"
          aria-label={`${activeCount} active filter${activeCount === 1 ? '' : 's'}`}
        >
          {activeCount}
        </span>
      )}
    </Button>
  );
};
